import { useMemo } from "react";

export const useDataTransforms = (declarationData) => {
  // Helper function to calculate months difference
  const calculateMonthsDifference = (fromDate, toDate) => {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    const yearDiff = to.getFullYear() - from.getFullYear();
    const monthDiff = to.getMonth() - from.getMonth();
    return yearDiff * 12 + monthDiff + 1;
  };

  // Get status from API and format it for display
  const getStatusDisplay = (status) => {
    switch (status) {
      case "APPROVAL_AWAITING":
        return {
          text: "Approval Awaiting",
          className: "bg-yellow-200 text-yellow-900 px-3 py-1 rounded text-xs",
        };
      case "REVIEWED":
        return {
          text: "Reviewed",
          className: "bg-green-200 text-green-900 px-3 py-1 rounded text-xs",
        };
      case "REJECTED":
        return {
          text: "Rejected",
          className: "bg-red-200 text-red-900 px-3 py-1 rounded text-xs",
        };
      default:
        return {
          text: "No Status",
          className: "px-3 py-1 rounded text-xs",
        };
    }
  };

  // Helper function to get comment drawer title and subtitle based on the key
  const getCommentDrawerTitles = (commentKey) => {
    if (commentKey.startsWith('house-rent-')) {
      const index = parseInt(commentKey.split('-')[2]);
      const rent = transformedHouseRent[index];
      return {
        title: "House Rent Paid Details",
        subtitle: rent?.period || "House Rent"
      };
    } else if (commentKey.startsWith('80c-')) {
      const index = parseInt(commentKey.split('-')[1]);
      const investment = declarationData?.investments_80C?.[index];
      return {
        title: "80C Investments",
        subtitle: investment?.label || "Investment"
      };
    } else if (commentKey.startsWith('80d-')) {
      const index = parseInt(commentKey.split('-')[1]);
      const exemption = declarationData?.exemptions_80D?.[index];
      return {
        title: "80D Exemptions",
        subtitle: exemption?.label || "Exemption"
      };
    } else if (commentKey.startsWith('other-income-')) {
      const keyPart = commentKey.replace('other-income-', '');
      const otherIncomeLabels = {
        'income_from_other_sources': 'Income from other sources',
        'interest_from_savings': 'Interest Earned from Savings Deposit',
        'interest_from_fixed_deposits': 'Interest Earned from Fixed Deposit',
        'interest_from_national_savings': 'Interest Earned from National Savings Certificates'
      };
      return {
        title: "Other Income",
        subtitle: otherIncomeLabels[keyPart] || "Other Income"
      };
    } else if (commentKey.startsWith('other-')) {
      const index = parseInt(commentKey.split('-')[1]);
      const investment = declarationData?.other_investments?.[index];
      return {
        title: "Other Investments & Exemptions",
        subtitle: investment?.label || "Investment"
      };
    } else if (commentKey === 'home-loan') {
      return {
        title: "Home Loan",
        subtitle: "Home Loan Interest & Principal"
      };
    } else if (commentKey.startsWith('rental-annual-rent-')) {
      return {
        title: "Rental Income",
        subtitle: "Annual Rent Received"
      };
    } else if (commentKey.startsWith('rental-municipal-taxes-')) {
      return {
        title: "Rental Income", 
        subtitle: "Municipal Taxes Paid"
      };
    } else if (commentKey.startsWith('rental-interest-')) {
      return {
        title: "Rental Income",
        subtitle: "Interest Paid on Home Loan"
      };
    } else if (commentKey.startsWith('rental-principal-')) {
      return {
        title: "Rental Income",
        subtitle: "Principal Paid on Home Loan"
      };
    } else if (commentKey.startsWith('home-loan-principal-')) {
      return {
        title: "Self Occupied Property",
        subtitle: "Principal Paid on Home Loan"
      };
    } else if (commentKey.startsWith('home-loan-interest-')) {
      return {
        title: "Self Occupied Property",
        subtitle: "Interest Paid on Home Loan"
      };
    } else if (commentKey.startsWith('rental-income-')) {
      const index = parseInt(commentKey.split('-')[2]);
      return {
        title: "Rental Income",
        subtitle: "Rental Income Details"
      };
    }
    
    return {
      title: "Comments",
      subtitle: "Investment Proof Comments"
    };
  };

  // Transform house rent data
  const transformedHouseRent = useMemo(() => {
    return declarationData?.house_rent?.map(rent => {
      const months = calculateMonthsDifference(rent.from_date, rent.to_date);
      const declaredMonthlyAmount = parseFloat(rent.rent_amount);
      const actualMonthlyAmount = parseFloat(rent.actual_amount || 0);
      const totalDeclaredAmount = declaredMonthlyAmount * months;
      const totalActualAmount = actualMonthlyAmount * months;
      const fromDate = new Date(rent.from_date);
      const toDate = new Date(rent.to_date);
      
      return {
        ...rent,
        period: `${fromDate.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })} - ${toDate.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}`,
        declaredAmount: `₹${totalDeclaredAmount.toLocaleString()}`,
        actualAmount: `₹${totalActualAmount.toLocaleString()}`,
        perMonth: `₹${declaredMonthlyAmount.toLocaleString()} / month`,
        actualPerMonth: `₹${actualMonthlyAmount.toLocaleString()} / month`,
        location: rent.address || "--",
        landlordName: rent.landlord_name || "--",
        landlordPan: rent.landlord_pan || "--",
        isMetro: rent.is_metro === "1"
      };
    }) || [];
  }, [declarationData?.house_rent]);

  return {
    transformedHouseRent,
    getStatusDisplay,
    getCommentDrawerTitles,
  };
};
